import type { CreateQueryClientOptions } from '@pa/api'
import { AuthStore, createStaffSession, type AuthClient } from '@pa/staff'
import type { RouterHistory } from '@tanstack/react-router'
import { createImpersonation, endImpersonation } from '@/features/impersonation'
import { createStaffQueryClient, setupApi } from './api'
import { App } from './App'
import type { StaffEnv } from './env'
import { createAppRouter } from './router'

/**
 * Builds the clinic app once: auth store, query client, session, API config and router (boot, tests and stories use
 * the same wiring).
 */
export function createClinicApp(options: {
  env: StaffEnv
  authClient: AuthClient
  history?: RouterHistory
  query?: CreateQueryClientOptions
  /** Leaving for the admin site (Exit, an ended impersonation): `window.location.assign` unless a test passes a spy. */
  assign?: (href: string) => void
}) {
  const { env, authClient, history } = options
  const queryClient = createStaffQueryClient(options.query)
  const auth = new AuthStore(authClient)
  const impersonation = createImpersonation({
    adminOrigin: env.VITE_ADMIN_APP_ORIGIN,
    ...(options.assign && { assign: options.assign }),
  })
  const built: { router?: ReturnType<typeof createAppRouter> } = {}
  const getRouter = () => {
    if (!built.router) throw new Error('the router is not built yet')
    return built.router
  }
  const session = createStaffSession({
    auth,
    queryClient,
    getRouter,
    // Sign out while impersonating ends the session first (#36 `reason: sign_out`, Frontend §5.1, §6.4).
    beforeSignOut: () => endImpersonation(impersonation, 'sign_out'),
    // `impersonation_ended` on any call (expired, ended elsewhere): drop it → the admin site's S-04 with the notice,
    // or "This impersonation has ended" when the clinic wasn't known yet (Frontend §6.4 steps 3 and 7).
    onAccessError: (error) => {
      if (error.code !== 'impersonation_ended') return false
      const href = impersonation.adminReturnHref()
      impersonation.clear()
      if (href) impersonation.assign(href)
      else void getRouter().navigate({ href: '/no-access?reason=impersonation-ended', replace: true })
      return true
    },
  })
  setupApi(env, auth, session, (path) => impersonation.headerFor(path))
  const router = createAppRouter({ queryClient, env, auth, impersonation }, history)
  built.router = router
  const element = (
    <App queryClient={queryClient} router={router} auth={auth} session={session} impersonation={impersonation} />
  )
  return { queryClient, router, auth, session, impersonation, element }
}
